// src/pages/Quizzes.js
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import InputGroup from "react-bootstrap/InputGroup";
import FormControl from "react-bootstrap/FormControl";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import Dropdown from "react-bootstrap/Dropdown";
import {
  Trophy,
  Clock,
  Play,
  Target,
  TrendingUp,
  Zap,
  Search,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { getMe, getQuizResults, listModules, getModule } from "../api/client";
import { useTranslation } from "../hooks/useTranslation";

const SIDEBAR_WIDTH = 280;
const SIDEBAR_COLLAPSED = 48;

export default function Quizzes() {
  const { t } = useTranslation();
  
  const [user, setUser] = useState(null);
  const [myResults, setMyResults] = useState([]);

  const [modules, setModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);

  const [loading, setLoading] = useState(true);
  const [loadingModule, setLoadingModule] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [activeTab, setActiveTab] = useState("all");
  const [collapsed, setCollapsed] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const moduleIdFromUrl = searchParams.get("module");

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const asideWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  // User + Module-Liste laden
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const [me, mods] = await Promise.all([getMe(), listModules()]);
        if (!me || !me.id) {
          setUser(null);
        } else {
          setUser(me);
          const results = await getQuizResults(me.id);
          setMyResults(Array.isArray(results) ? results : []);
        }

        const sorted =
          (Array.isArray(mods) ? mods : []).sort(
            (a, b) => (a.order_index || 0) - (b.order_index || 0)
          ) || [];
        setModules(sorted);

        // Falls kein Modul in der URL, erstes Modul wählen (Standard: Modul 1)
        if (!moduleIdFromUrl && sorted.length) {
          setSearchParams({ module: sorted[0].id });
        }
      } catch (e) {
        console.error("Quizzes initial load error:", e);
        setError(t.modules.loadError);
        setUser(null);
        setModules([]);
        setMyResults([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Aktives Modul laden, sobald moduleId in der URL vorhanden ist
  useEffect(() => {
    const effectiveModuleId = moduleIdFromUrl || "M1_WR_1K";
    (async () => {
      try {
        setLoadingModule(true);
        const mod = await getModule(effectiveModuleId);
        setActiveModule(mod || null);
      } catch (e) {
        console.error("getModule in Quizzes error:", e);
        setActiveModule(null);
      } finally {
        setLoadingModule(false);
      }
    })();
  }, [moduleIdFromUrl]);

  const handleSelectModule = (id) => {
    setSearchParams({ module: id });
  };

  // Lektionen des aktiven Moduls als "Quiz-Definitionen"
  const quizzes = useMemo(() => {
    if (!activeModule || !Array.isArray(activeModule.lessons)) return [];
    return activeModule.lessons
      .slice()
      .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
      .map((lesson) => ({
        id: lesson.id,
        title: lesson.title,
        description: lesson.description,
        category: lesson.category || "grundlagen",
        difficulty: lesson.difficulty || "beginner",
        quiz_type: "practice",
        time_limit_seconds: (lesson.duration_minutes || 20) * 60,
        questions_count: Array.isArray(lesson.exercises)
          ? lesson.exercises.length
          : 0,
      }));
  }, [activeModule]);

  // Stats-Helfer
  const getMyBestScore = (quizId) => {
    const results = myResults.filter((r) => r.quiz_id === quizId);
    if (!results.length) return null;
    return Math.max(...results.map((r) => r.percentage || 0));
  };

  const getMyAttempts = (quizId) =>
    myResults.filter((r) => r.quiz_id === quizId).length;

  const getCategoryColor = (category) => {
    const colors = {
      grundlagen: "primary",
      bilanzierung: "secondary",
      kostenrechnung: "info",
      controlling: "warning",
      steuern: "danger",
      anwendung: "success",
    };
    return colors[category] || "secondary";
  };

  const getQuizTypeIcon = (type) => {
    switch (type) {
      case "live":
        return <Zap size={16} />;
      case "homework":
        return <Target size={16} />;
      default:
        return <Play size={16} />;
    }
  };

  const filteredQuizzes = quizzes.filter((quiz) => {
    const matchesSearch =
      quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quiz.description &&
        quiz.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory =
      selectedCategory === "all" || quiz.category === selectedCategory;
    const matchesType = activeTab === "all" || quiz.quiz_type === activeTab;
    return matchesSearch && matchesCategory && matchesType;
  });

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (!user) {
    return (
      <Container className="py-5 text-center">
        <p>{t.common.error}</p>
      </Container>
    );
  }

  const currentModuleTitle = modules.find(m => m.id === moduleIdFromUrl)?.title || t.modules.selectModule;

  return (
    <div style={{ minHeight: "100vh", width: "100%", position: "relative" }}>
      {/* Desktop: Modul-Sidebar */}
      {isDesktop && (
        <aside
          className="lesson-sidebar animate-slide-in quizzes-aside"
          style={{
            width: asideWidth,
            flexShrink: 0,
            position: "fixed",
            top: 0,
            left: "240px",
            height: "100vh",
            borderRight: "1px solid #eee",
            background: "#fff",
            zIndex: 999,
            overflow: "hidden",
            transition: "width 280ms ease-in-out, left 280ms ease-in-out",
          }}
        >
          <div className="d-flex align-items-center justify-content-between px-2 py-2 border-bottom">
            {!collapsed && <h5 className="m-0">{t.modules.title}</h5>}
            <Button
              variant="light"
              size="sm"
              onClick={() => setCollapsed(!collapsed)}
              aria-label={collapsed ? "Sidebar öffnen" : "Sidebar schließen"}
            >
              {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </Button>
          </div>

          {!collapsed && (
            <div
              className="p-2"
              style={{
                height: "calc(100% - 42px)",
                overflowY: "auto",
              }}
            >
              <Card className="border-0">
                <Row as="div" className="g-1">
                  {modules.map((m) => {
                    const isActive = (moduleIdFromUrl || "M1_WR_1K") === m.id;
                    return (
                      <Col xs={12} key={m.id}>
                        <Card
                          as="button"
                          type="button"
                          onClick={() => handleSelectModule(m.id)}
                          className="w-100 text-start border-0 shadow-none"
                          style={{
                            borderRadius: 8,
                            padding: "6px 8px",
                            backgroundColor: isActive
                              ? "rgba(30,136,229,0.08)"
                              : "transparent",
                            cursor: "pointer",
                          }}
                        >
                          <div className="d-flex align-items-start justify-content-between">
                            <div className="flex-grow-1 pe-2 module-list-title">
                              {m.title}
                            </div>
                            {typeof m.order_index === "number" && (
                              <Badge
                                bg="light"
                                text="dark"
                                className="flex-shrink-0"
                              >
                                {m.order_index}
                              </Badge>
                            )}
                          </div>
                        </Card>
                      </Col>
                    );
                  })}
                  {modules.length === 0 && (
                    <div className="text-muted small px-2 py-3">
                      {t.modules.noModules}
                    </div>
                  )}
                </Row>
              </Card>
            </div>
          )}
        </aside>
      )}

      {/* Hauptbereich */}
      <main
        className="quizzes-main"
        style={{
          marginLeft: isDesktop ? `${240 + asideWidth}px` : '0',
          background: "#f8f9fa",
          minHeight: "100vh",
          transition: "margin-left 280ms ease-in-out",
        }}
      >
        <Container className="py-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="mb-2">{t.quizzes.title}</h1>
            <p className="text-muted mb-0">
              {t.quizzes.subtitle}
            </p>
            {activeModule && (
              <p className="text-muted mt-2 mb-0">
                {t.quizzes.activeModule}:{" "}
                <strong>
                  {activeModule.title} ({activeModule.year_level}. {t.modules.class}, {t.modules.semester} {activeModule.semester})
                </strong>
              </p>
            )}
          </div>

          {/* Mobile: Module Dropdown */}
          {!isDesktop && modules.length > 0 && (
            <div className="mb-3">
              <Dropdown className="module-select-dropdown">
                <Dropdown.Toggle 
                  variant="outline-secondary"
                  className="w-100 text-start d-flex justify-content-between align-items-center"
                  style={{
                    height: "auto",
                    minHeight: "48px",
                    padding: "12px 16px",
                    fontSize: "1rem",
                    border: "1px solid #ced4da",
                    backgroundColor: "#fff",
                    color: "#495057"
                  }}
                >
                  <span style={{ 
                    flex: 1,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: "1.4",
                    paddingRight: "8px"
                  }}>
                    {currentModuleTitle}
                  </span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {modules.map((mod) => (
                    <Dropdown.Item
                      key={mod.id}
                      active={mod.id === moduleIdFromUrl}
                      onClick={() => handleSelectModule(mod.id)}
                      style={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: "10px 16px"
                      }}
                    >
                      <div>
                        <div className="fw-semibold" style={{ lineHeight: "1.3" }}>
                          {mod.title}
                        </div>
                        <small className="text-muted d-block mt-1">
                          {mod.year_level}. {t.modules.class} • {t.modules.semester} {mod.semester}
                        </small>
                      </div>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          )}

          {/* Stats Cards */}
          <Row className="g-3 mb-4">
            <Col lg={3} md={6} sm={6} xs={12}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3"
                    style={{ backgroundColor: "#e3f2fd" }}
                  >
                    <Trophy size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">{myResults.length}</h3>
                    <p className="text-muted mb-0 small">{t.quizzes.completed}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} sm={6} xs={12}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3"
                    style={{ backgroundColor: "#f3e5f5" }}
                  >
                    <TrendingUp size={24} className="text-purple" />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">
                      {myResults.length
                        ? Math.round(
                            myResults.reduce(
                              (acc, r) => acc + (r.percentage || 0),
                              0
                            ) / myResults.length
                          )
                        : 0}
                      %
                    </h3>
                    <p className="text-muted mb-0 small">{t.quizzes.average}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} sm={6} xs={12}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3"
                    style={{ backgroundColor: "#fff3e0" }}
                  >
                    <Trophy size={24} className="text-warning" />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">
                      {myResults.reduce((acc, r) => acc + (r.score || 0), 0)}
                    </h3>
                    <p className="text-muted mb-0 small">{t.quizzes.pointsEarned}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={3} md={6} sm={6} xs={12}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="d-flex align-items-center">
                  <div
                    className="rounded-circle p-3 me-3"
                    style={{ backgroundColor: "#e8f5e9" }}
                  >
                    <Target size={24} className="text-success" />
                  </div>
                  <div>
                    <h3 className="mb-0 fw-bold">
                      {myResults.length
                        ? Math.max(...myResults.map((r) => r.percentage || 0))
                        : 0}
                      %
                    </h3>
                    <p className="text-muted mb-0 small">{t.quizzes.bestPerformance}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Modul wird geladen */}
          {loadingModule && (
            <div className="py-5 text-center">
              <Spinner animation="border" />
            </div>
          )}

          {/* Falls kein Modul / Fehler */}
          {!loadingModule && !activeModule && (
            <Card className="border-0 shadow-sm">
              <Card.Body className="py-5 text-center">
                <p className="text-danger mb-2">
                  {error || t.modules.loadError}
                </p>
                <p className="text-muted mb-0">
                  {isDesktop ? t.modules.selectModuleDesktop : t.modules.selectModuleMobile}
                </p>
              </Card.Body>
            </Card>
          )}

          {!loadingModule && activeModule && (
            <>
            
             

              {/* Quiz-Karten */}
              <Row className="g-3">
                {filteredQuizzes.map((quiz) => {
                  const score = getMyBestScore(quiz.id);
                  const attempts = getMyAttempts(quiz.id);

                  return (
                    <Col lg={4} md={6} sm={12} key={quiz.id}>
                      <Card className="h-100 border-0 shadow-sm">
                        <Card.Body className="d-flex flex-column">
                          <div>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                              <Badge bg={getCategoryColor(quiz.category)}>
                                {quiz.category}
                              </Badge>
                              <div className="d-flex align-items-center">
                                {getQuizTypeIcon(quiz.quiz_type)}
                                <span className="ms-1 small">
                                  {quiz.quiz_type}
                                </span>
                              </div>
                            </div>

                            <h5 className="mb-2">{quiz.title}</h5>
                            <p className="text-muted small mb-3">
                              {quiz.description || "Keine Beschreibung verfügbar"}
                            </p>

                            <div className="d-flex align-items-center text-muted small mb-2">
                              <Clock size={14} className="me-1" />
                              {Math.floor((quiz.time_limit_seconds || 600) / 60)} {t.quizzes.minutes} {t.quizzes.timeLimit}
                            </div>
                            <div className="d-flex align-items-center text-muted small mb-3">
                              <Target size={14} className="me-1" />
                              {quiz.questions_count} {t.quizzes.questions}
                            </div>

                            {attempts > 0 && (
                              <div className="bg-light p-2 rounded mb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                  <small className="text-muted">
                                    {t.quizzes.bestpoints}
                                  </small>
                                  <Badge bg="success">{score}%</Badge>
                                </div>
                                <small className="text-muted">
                                  {t.quizzes.tries} {attempts}
                                </small>
                              </div>
                            )}
                          </div>

                          <div className="mt-auto">
                            <Link
                              to={`/quiz/${encodeURIComponent(
                                activeModule.id
                              )}/${encodeURIComponent(quiz.id)}`}
                            >
                              <Button variant="primary" size="sm" className="w-100">
                                <Play size={16} className="me-1" />
                                {attempts > 0 ? t.quizzes.retryQuiz : t.quizzes.startQuiz}
                              </Button>
                            </Link>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </Container>
      </main>
    </div>
  );
}
